import { translate } from '@rhino-project/core/i18n';
import {
  ErrorBoundary as RollbarErrorBounday,
  Provider as RollbarProvider
} from '@rollbar/react';
import { useRollbarConfig } from './lib/rollbar';
import { RollbarReporterProvider } from './lib/RollbarReporterProvider';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LocaleProvider, NotFoundPage } from '@rhino-project/ui-heroui';
import { Spinner, toast } from '@heroui/react';
import { RhinoProvider, useRhinoContext } from '@rhino-project/core';
import { NetworkServerError } from '@rhino-project/core/lib';
import { RhinoDevTool } from '@rhino-project/ui-heroui';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import {
  defaultAnalyticsClient,
  subscribeRouterPageViews
} from '@rhino-project/core/analytics';

// Import the generated models
import type { GeneratedModels } from './models/rhino';

declare module '@rhino-project/core' {
  type ReadPayloads = {
    [K in keyof GeneratedModels]: GeneratedModels[K]['read'];
  };

  interface Resources extends ReadPayloads {}
  interface RhinoModels extends GeneratedModels {}
}

// Import the generated route tree
import { routeTree } from './routeTree.gen';
import CustomContexts from './contexts/CustomContexts';
import { getInitialBaseOwnerId } from './utils/initialBaseOwner';
import { retryUnlessClientError } from './utils/queryRetry';

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const handleNetworkError = (error: unknown) => {
  if (error instanceof NetworkServerError) {
    toast.danger(translate('frontend.messages.request_error'), {
      description: translate(
        'frontend.messages.sorry_the_server_returned_an_error_during_your_request'
      )
    });
  }
};

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: retryUnlessClientError } },
  queryCache: new QueryCache({ onError: handleNetworkError }),
  mutationCache: new MutationCache({ onError: handleNetworkError })
});

// Create a new router instance
const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
  defaultPendingComponent: () => (
    <div className="flex h-full items-center justify-center">
      <Spinner size="md" />
    </div>
  ),
  defaultPendingMs: 200,
  defaultPendingMinMs: 300,
  defaultPreload: 'intent',
  scrollRestoration: true,
  // This will be set after we wrap the app in an RhinoProvider
  context: { rhino: undefined! }
});

// One page view per resolved navigation, for the life of the app.
subscribeRouterPageViews(router, defaultAnalyticsClient);

// Render the app
const RootUI = () => {
  const rhino = useRhinoContext();

  return <RouterProvider router={router} context={{ rhino }} />;
};

const Root = () => {
  const rollbarConfig = useRollbarConfig();

  // Rollbar wraps everything so its error boundary also catches failures in
  // RhinoProvider itself (e.g. the session fetch). The signed-in user is
  // attached to Rollbar by useIdentifyRollbar in the router root, and
  // RollbarReporterProvider routes package-level error boundaries to Rollbar.
  return (
    <RollbarProvider config={rollbarConfig}>
      <RollbarErrorBounday>
        <RollbarReporterProvider>
          {/* initialBaseOwnerId restores the URL's organization (falling back
              to the remembered one) before the first render, so the app never
              flashes (or queries against) a different organization before the
              router corrects it. */}
          <LocaleProvider>
            <RhinoProvider
              queryClient={queryClient}
              analyticsClient={defaultAnalyticsClient}
              initialBaseOwnerId={getInitialBaseOwnerId}
            >
              <CustomContexts>
                <RhinoDevTool />
                <ReactQueryDevtools
                  initialIsOpen={false}
                  buttonPosition="bottom-right"
                  position="bottom"
                />
                <RootUI />
              </CustomContexts>
            </RhinoProvider>
          </LocaleProvider>
        </RollbarReporterProvider>
      </RollbarErrorBounday>
    </RollbarProvider>
  );
};

export default Root;
